import { CheckCircle2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader } from '../ui/card';
import { useLanguage } from '../../contexts/LanguageContext';

interface SuccessAction {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary';
}

interface SuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'listingPublished' | 'listingUpdated' | 'profileSaved' | 'contactSent';
  actions?: SuccessAction[];
}

export function SuccessModal({ isOpen, onClose, type, actions = [] }: SuccessModalProps) {
  const { t } = useLanguage();

  if (!isOpen) return null;

  const getContent = () => {
    switch (type) {
      case 'listingPublished':
        return {
          title: t('dealer.success.listingPublished.title'),
          message: t('dealer.success.listingPublished.message'),
          defaultActions: [
            {
              label: t('dealer.success.viewListing'),
              onClick: () => console.log('View listing'),
              variant: 'secondary' as const
            },
            {
              label: t('dealer.success.addAnother'),
              onClick: () => console.log('Add another car'),
              variant: 'primary' as const
            }
          ]
        };
      case 'listingUpdated':
        return {
          title: t('dealer.success.listingUpdated.title'),
          message: t('dealer.success.listingUpdated.message'),
          defaultActions: [
            {
              label: t('dealer.success.backToListings'),
              onClick: () => console.log('Back to listings'),
              variant: 'primary' as const
            }
          ]
        };
      case 'profileSaved':
        return {
          title: t('dealer.success.profileSaved.title'),
          message: t('dealer.success.profileSaved.message'),
          defaultActions: [
            {
              label: t('dealer.success.editProfile'),
              onClick: () => console.log('Edit profile'),
              variant: 'secondary' as const
            },
            {
              label: t('dealer.success.backToListings'),
              onClick: () => console.log('Back to listings'),
              variant: 'primary' as const
            }
          ]
        };
      case 'contactSent':
        return {
          title: t('dealer.success.contactSent.title'),
          message: t('dealer.success.contactSent.message'),
          defaultActions: []
        };
      default:
        return {
          title: 'Success',
          message: 'Operation completed successfully.',
          defaultActions: []
        };
    }
  };

  const content = getContent();
  const finalActions = actions.length > 0 ? actions : content.defaultActions;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      onClick={handleOverlayClick}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300" />
      
      <Card className="relative z-10 w-full max-w-md mx-4 border-[#f91bdc]/20 shadow-xl animate-in zoom-in-95 duration-300">
        <CardHeader className="relative pb-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onClose}
            className="absolute right-4 top-4 h-8 w-8 p-0 hover:bg-gray-100"
          >
            <X size={18} />
          </Button>
        </CardHeader>
        
        <CardContent className="text-center space-y-6 pb-8">
          {/* Success Icon */}
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle2 size={40} className="text-green-600" />
          </div>
          
          {/* Success Message */}
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-gray-900">
              {content.title}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {content.message}
            </p>
          </div>

          {/* Action Buttons */}
          {finalActions.length > 0 && (
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              {finalActions.map((action, index) => (
                <Button
                  key={index}
                  onClick={() => {
                    action.onClick();
                    onClose();
                  }}
                  variant={action.variant === 'secondary' ? 'outline' : 'default'}
                  className={
                    action.variant === 'secondary' 
                      ? 'flex-1 border-[#f91bdc] text-[#f91bdc] hover:bg-[#f91bdc]/10' 
                      : 'flex-1 bg-[#f91bdc] hover:bg-[#e015c9] text-white'
                  }
                >
                  {action.label}
                </Button>
              ))}
            </div>
          )}

          {/* Close Button (if no custom actions) */}
          {finalActions.length === 0 && (
            <Button 
              onClick={onClose}
              className="w-full bg-[#f91bdc] hover:bg-[#e015c9] text-white"
            >
              {t('dealer.success.close')}
            </Button>
          )}

          {/* Additional Info for Published Listings */}
          {type === 'listingPublished' && (
            <div className="bg-[#f91bdc]/5 rounded-lg p-4 mt-4">
              <p className="text-sm text-gray-700">
                Your listing is now searchable and customers can start the purchase process immediately.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}